import Immutable from 'immutable';
import React from 'react';

import GraphQL from '~/types/graphql';

import AdminAddConductorCustomerDomainsModal from '~/components/app/AdminAddConductorCustomerDomainsModal';
import BasicIcon, {
	BasicIconType,
} from '~/components/patterns/icons/BasicIcon';
import Button, {
	ButtonSize,
	ButtonStyle,
} from '~/components/patterns/buttons/Button';
import ButtonsGroup from '~/components/patterns/buttons/ButtonsGroup';
import Ellipsis from '~/components/patterns/values/Ellipsis';
import NewDatatable, {
	type NewDatatableColumnDefinitions,
} from '~/components/app/NewDatatable';
import PlainTextFieldFilter from '~/components/logic/datatables/PlainTextFieldFilter';
import WithPermission from '~/components/logic/access/WithPermission';

import {
	useKingdomSettingsConductorCustomerDomainsQuery,
} from './KingdomSettingsConductorCustomerDomainsSection.gql';

import useOpenModal from '~/hooks/useOpenModal';

import {
	ObjectType,
} from '~/model/permissions';



const KingdomSettingsConductorCustomerDomainsSection: React.FC = () => {
	const [filter, setFilter] = React.useState(Immutable.Map({
		conductorCustomerDomain: '',
	}));

	const { data } = useKingdomSettingsConductorCustomerDomainsQuery();

	const conductorCustomerDomains = data?.kingdomSettings?.conductorCustomerDomains ?? null;

	const handleOpenAddConductorCustomerDomainsModal = useOpenModal(
		() => (
			<AdminAddConductorCustomerDomainsModal />
		),
		[],
	);

	const updateFilter = React.useCallback(
		(filterUpdate) => {
			setFilter((filter) => filter.merge(filterUpdate));
		},
		[],
	);

	const rows = React.useMemo(
		() => {
			return (conductorCustomerDomains ?? []).filter(
				(row) => {
					return row.includes(filter.get('conductorCustomerDomain'));
				},
			);
		},
		[
			conductorCustomerDomains,
			filter,
		],
	);

	const columns: NewDatatableColumnDefinitions<typeof rows> = React.useMemo(
		() => {
			return [
				{
					name: 'conductorCustomerDomain',
					render: {
						cell: ({ row }) => (
							<Ellipsis>
								{row}
							</Ellipsis>
						),
						filter: ({ filterName, filterWidth, ref }) => (
							<PlainTextFieldFilter
								name={filterName}
								ref={ref}
								width={filterWidth}
							/>
						),
						label: () => 'Conductor customer domain',
					},
					width: 'fill',
				},
			];
		},
		[],
	);

	return (
		<NewDatatable
			buttons={(
				<ButtonsGroup>
					<WithPermission
						action={GraphQL.ActionWithPlatform.ManageConductorCustomerDomains}
						objectId="platform"
						objectType={ObjectType.Platform}
						showMessage={false}
					>
						{({ isAllowed, message: permissionMessage }) => (
							<Button
								disabled={isAllowed.yes === false}
								icon={(
									<BasicIcon type={BasicIconType.Plus} />
								)}
								onClick={handleOpenAddConductorCustomerDomainsModal}
								size={ButtonSize.Small}
								style={ButtonStyle.Hollow}
								tooltip={permissionMessage}
								uppercase={true}
							>
								Add Conductor customer domains
							</Button>
						)}
					</WithPermission>
				</ButtonsGroup>
			)}
			columns={columns}
			filter={filter}
			isLoading={conductorCustomerDomains === null}
			onFilterChange={updateFilter}
			rows={rows}
			title="Conductor customer domains"
		/>
	);
};



export default KingdomSettingsConductorCustomerDomainsSection;
